import { OtherSubjectsForm, PersonalDetailsForm, ReasonForm, Reason } from '@ha/domain';
import content from '@ha/hypotheken-afspraak.content.json';
import { MortgageAppointmentService } from '@ha/services/mortgage-appointment.service';
import { AnalyticsService, FormService } from '@ha/services';
import { ReasonComponent } from '@ha/reason/reason.component';
import { PersonalDetailsComponent } from '@ha/personal-details/personal-details.component';
import { OtherSubjectsComponent } from '@ha/other-subjects/other-subjects.component';
import { CtaComponent } from '@ha/cta/cta.component';

import { Component, inject, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, debounceTime } from 'rxjs';
import { CommonModule } from '@angular/common';

import { ApContentModule, ApContentService } from '@ap/content';
import { ApWindowService } from '@ap/cdk';
import { ApContactDetailsComponent, ContactDetailsForm } from '@ap/personal-details';
import { AddressForm, ApDutchAddressComponent } from '@ap/dutch-address';
import { ApAnalyticsModule } from '@ap/analytics';
import { ApPanelComponent } from '@ap/panel';
import { ApButtonComponent, ApSubmitTextComponent } from '@ap/button';

@Component({
    selector: 'hypotheken-afspraak',
    templateUrl: './hypotheken-afspraak.component.html',
    standalone: true,
    imports: [
        ApAnalyticsModule,
        ApPanelComponent,
        CommonModule,
        ApContentModule,
        FormsModule,
        ReactiveFormsModule,
        ApDutchAddressComponent,
        ApButtonComponent,
        ApSubmitTextComponent,
        ApContactDetailsComponent,
        ReasonComponent,
        PersonalDetailsComponent,
        OtherSubjectsComponent,
        CtaComponent
    ]
})
export class HypothekenAfspraakComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly mortgageAppointmentService = inject(MortgageAppointmentService);
    private readonly windowService = inject(ApWindowService);
    private readonly formService = inject(FormService);
    private readonly analyticsService = inject(AnalyticsService);

    isLoading: boolean = false;
    showNotificationSubject = new BehaviorSubject<boolean>(false);
    reasonFormGroup: FormGroup<ReasonForm>;
    personalDetailsFormGroup: FormGroup<PersonalDetailsForm>;
    addressFormGroup: FormGroup<AddressForm>;
    contactDetailsFormGroup: FormGroup<ContactDetailsForm>;
    otherSubjectsFormGroup: FormGroup<OtherSubjectsForm>;
    reason: Reason;

    ngOnInit() {
        this.contentService.addContentItems(content);
        this.createFormGroups();

        this.reasonFormGroup?.valueChanges.subscribe((reason) => {
            this.setShowNotification(reason.reason);
            if (reason.reason !== this.reason) {
                this.reason = reason.reason;
                this.reasonFormGroup.controls.houseReason.reset();
                this.reasonFormGroup.controls.refinance.reset();
            }
        });
        this.subscribeNotification();
    }

    private createFormGroups(): void {
        this.reasonFormGroup = this.formService.createReasonFormGroup();
        this.personalDetailsFormGroup = this.formService.createPersonalDetailsFormGroup();
        this.addressFormGroup = this.formService.createDutchAddressFormGroup();
        this.contactDetailsFormGroup = this.formService.createContactDetailsFormGroup();
        this.otherSubjectsFormGroup = this.formService.createOtherSubjectsFormGroup();
    }

    private subscribeNotification(): void {
        this.showNotificationSubject.pipe(debounceTime(2500)).subscribe((result) => {
            if (result === true) {
                this.analyticsService.sendFormAnalytics(this.reasonFormGroup);
            }
        });
    }

    private setShowNotification(reason: Reason): void {
        if (reason) {
            this.showNotificationSubject.next(Reason.DIFFERENT_QUESTION === reason);
        }
    }

    onSubmit(): void {
        if (
            this.formService.verifyFormGroups(
                this.reasonFormGroup,
                this.personalDetailsFormGroup,
                this.addressFormGroup,
                this.contactDetailsFormGroup,
                this.otherSubjectsFormGroup
            )
        ) {
            this.isLoading = true;
            this.mortgageAppointmentService
                .createRequest(
                    this.reasonFormGroup,
                    this.personalDetailsFormGroup,
                    this.addressFormGroup,
                    this.contactDetailsFormGroup,
                    this.otherSubjectsFormGroup
                )
                .subscribe({
                    next: () => {
                        this.analyticsService.sendClickAnalytics('submit.call_for_appointment');
                        this.analyticsService.sendFormAnalytics(this.reasonFormGroup);
                    },
                    error: () => {
                        this.isLoading = false;
                        this.windowService.location_href(this.contentService.get('confirmation.error'));
                    },
                    complete: () => {
                        this.isLoading = false;
                        this.windowService.location_href(this.contentService.get(`confirmation.success`));
                    }
                });
        }
    }
}
